import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkboxes, DateInput, Fieldset, Input, Radios, Select, isoToParts, partsToIso } from '../../../components/idsk';
import { FIELD_COMPARISONS, RuleSummarizer, operatorsFor, type EvaluationContext, type FieldDef, type FormDefinition, type JsonValue, type LocalizedText, type Operator, type RuleName } from '../../../form-engine';
import { text } from '../../../form-engine/types';
import { toCanonicalNumber, displayNumber } from '../../../form-engine/react/numbers';
import { emptyLeaf, fromEditor, newId, type EditorGroup, type EditorLeaf, type EditorRule } from './definitionOps';

export interface Candidate {
  ref: string;
  field: FieldDef;
  sameRow: boolean;
  /** the field the rule belongs to (a validation rule may refer to it) */
  self?: boolean;
}

export interface RuleEditorProps {
  id: string;
  rule: RuleName;
  value: EditorRule | null | 'unsupported';
  unsupportedSummary?: string | null;
  onChange: (value: EditorRule | null) => void;
  candidates: Candidate[];
  definition: FormDefinition;
  context: EvaluationContext;
  /** custom-field mode offers team-member comparisons (the team is known). */
  teamKnown: boolean;
  errors: Record<string, string>;
  /** the rule of an option (visibleWhen): its texts and summary are about the option with this label */
  optionLabel?: LocalizedText;
  /** the legend instead of the rule's own (e.g. "Kedy sa možnosť 2 ponúkne") */
  legend?: string;
  /** explains the rule under its legend */
  hint?: string;
  /** more settings of a rule that is set (e.g. the message of a validation rule) */
  children?: ReactNode;
}

type Lang = 'sk' | 'en';

/** Complete leaf rows (field, operator and – where needed – a value or the field compared with). */
export function leafComplete(l: EditorLeaf): boolean {
  if (!l.field || !l.op) return false;
  if (l.op === 'empty' || l.op === 'notEmpty') return true;
  if (l.valueField !== undefined) return l.valueField !== '';
  if (l.value === undefined || l.value === null || l.value === '') return false;
  return !Array.isArray(l.value) || l.value.length > 0;
}

export function ruleComplete(rule: EditorRule): boolean {
  return rule.items.length > 0 && rule.items.every((x) => (x.kind === 'leaf' ? leafComplete(x) : x.items.length > 0 && x.items.every(leafComplete)));
}

/** Operators by the referenced field's type; TEAM_MEMBER comparisons only where the team is known. */
export function operatorChoices(field: FieldDef | undefined, teamKnown: boolean): Operator[] {
  if (!field) return [];
  const ops = operatorsFor(field.type) as Operator[];
  if (field.type === 'TEAM_MEMBER' && !teamKnown) return ops.filter((o) => o === 'empty' || o === 'notEmpty');
  return ops;
}

/** Structured, never free-text, editor of one rule (visibleWhen / enabledWhen / requiredWhen / validWhen). */
export function RuleEditor({ id, rule, value, unsupportedSummary, onChange, candidates, definition, context, teamKnown, errors, optionLabel, legend, hint, children }: RuleEditorProps) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const conditional = value !== null;
  const summarizer = useMemo(() => new RuleSummarizer(definition, context), [definition, context]);
  // texts of the rule of a field, or of an option ("Kedy sa možnosť ponúkne")
  const texts = optionLabel ? 'builder.rules.option' : `builder.rules.${rule}`;

  const summary = (() => {
    if (value === 'unsupported') return unsupportedSummary ?? '';
    if (!value) return t(`${texts}.always`);
    if (!ruleComplete(value)) return t('builder.rules.incomplete');
    if (optionLabel) return summarizer.optionSummary({ value: '__preview', label: optionLabel, visibleWhen: fromEditor(value) }, lang) ?? '';
    const temp: FieldDef = { key: '__preview', type: 'TEXT', label: { sk: '' }, [rule]: fromEditor(value) };
    return summarizer.summaryOf(temp, rule, lang) ?? '';
  })();

  const setRule = (next: EditorRule) => onChange(next);
  const updateItem = (index: number, item: EditorLeaf | EditorGroup) => {
    if (value === null || value === 'unsupported') return;
    setRule({ ...value, items: value.items.map((x, i) => (i === index ? item : x)) });
  };
  const removeItem = (index: number) => {
    if (value === null || value === 'unsupported') return;
    const items = value.items.filter((_, i) => i !== index);
    if (items.length === 0) onChange(null);
    else setRule({ ...value, items });
    requestAnimationFrame(() => document.getElementById(`${id}-add`)?.focus());
  };

  return (
    <Fieldset
      legend={optionLabel ? (legend ?? t(`${texts}.legend`)) : <h3 className="govuk-fieldset__heading">{legend ?? t(`${texts}.legend`)}</h3>}
      legendClassName={optionLabel ? 'govuk-fieldset__legend--s' : 'govuk-fieldset__legend--m'}
      className="app-rule-group"
    >
      {hint && <span className="govuk-hint">{hint}</span>}
      {value === 'unsupported' ? (
        <>
          <p className="govuk-body">{t('builder.rules.unsupported')}</p>
          <p className="govuk-body">
            <strong>{unsupportedSummary}</strong>
          </p>
          <Button variant="secondary" onClick={() => onChange({ combinator: 'all', items: [emptyLeaf()] })}>
            {t('builder.rules.replace')}
          </Button>
        </>
      ) : (
        <>
          <Radios
            name={`${id}-mode`}
            idPrefix={`${id}-mode`}
            legend={t(`${texts}.question`)}
            legendClassName="govuk-fieldset__legend--s"
            items={[
              { value: 'always', label: t(`${texts}.always`) },
              { value: 'conditions', label: t(`${texts}.conditions`) },
            ]}
            value={conditional ? 'conditions' : 'always'}
            onChange={(v) => onChange(v === 'always' ? null : { combinator: 'all', items: [emptyLeaf()] })}
          />
          {value && (
            <>
              <Radios
                name={`${id}-combinator`}
                idPrefix={`${id}-combinator`}
                legend={t('builder.rules.combinator')}
                legendClassName="govuk-fieldset__legend--s"
                inline
                items={[
                  { value: 'all', label: t('builder.rules.all') },
                  { value: 'any', label: t('builder.rules.any') },
                ]}
                value={value.combinator}
                onChange={(v) => setRule({ ...value, combinator: v as 'all' | 'any' })}
              />
              {value.items.map((item, index) =>
                item.kind === 'leaf' ? (
                  <ConditionRow
                    key={item.id}
                    id={`${id}-${index + 1}`}
                    n={index + 1}
                    leaf={item}
                    candidates={candidates}
                    context={context}
                    teamKnown={teamKnown}
                    errors={errors}
                    onChange={(leaf) => updateItem(index, leaf)}
                    onRemove={() => removeItem(index)}
                  />
                ) : (
                  <GroupRow
                    key={item.id}
                    id={`${id}-${index + 1}`}
                    n={index + 1}
                    group={item}
                    candidates={candidates}
                    context={context}
                    teamKnown={teamKnown}
                    errors={errors}
                    onChange={(group) => updateItem(index, group)}
                    onRemove={() => removeItem(index)}
                  />
                ),
              )}
              <div className="idsk-button-group">
                <Button
                  id={`${id}-add`}
                  variant="secondary"
                  onClick={() => {
                    setRule({ ...value, items: [...value.items, emptyLeaf()] });
                    requestAnimationFrame(() => document.getElementById(`${id}-${value.items.length + 1}-field`)?.focus());
                  }}
                >
                  {t('builder.rules.addCondition')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setRule({ ...value, items: [...value.items, { kind: 'group', id: newId(), combinator: 'any', items: [emptyLeaf()] }] });
                    requestAnimationFrame(() => document.getElementById(`${id}-${value.items.length + 1}-1-field`)?.focus());
                  }}
                >
                  {t('builder.rules.addGroup')}
                </Button>
              </div>
            </>
          )}
        </>
      )}
      <p className="govuk-body app-rule-summary" aria-live="polite" id={`${id}-summary`}>
        <span className="govuk-visually-hidden">{t('builder.rules.summaryLabel')} </span>
        <strong>{summary}</strong>
      </p>
      {value !== null && children}
    </Fieldset>
  );
}

function GroupRow({
  id,
  n,
  group,
  candidates,
  context,
  teamKnown,
  errors,
  onChange,
  onRemove,
}: {
  id: string;
  n: number;
  group: EditorGroup;
  candidates: Candidate[];
  context: EvaluationContext;
  teamKnown: boolean;
  errors: Record<string, string>;
  onChange: (g: EditorGroup) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Fieldset legend={t('builder.rules.groupLegend', { n })} legendClassName="govuk-fieldset__legend--s" className="app-rule-group">
      <Radios
        name={`${id}-combinator`}
        idPrefix={`${id}-combinator`}
        legend={t('builder.rules.combinator')}
        legendClassName="govuk-fieldset__legend--s"
        inline
        items={[
          { value: 'all', label: t('builder.rules.all') },
          { value: 'any', label: t('builder.rules.any') },
        ]}
        value={group.combinator}
        onChange={(v) => onChange({ ...group, combinator: v as 'all' | 'any' })}
      />
      {group.items.map((leaf, i) => (
        <ConditionRow
          key={leaf.id}
          id={`${id}-${i + 1}`}
          n={i + 1}
          leaf={leaf}
          candidates={candidates}
          context={context}
          teamKnown={teamKnown}
          errors={errors}
          onChange={(l) => onChange({ ...group, items: group.items.map((x, j) => (j === i ? l : x)) })}
          onRemove={() => {
            const items = group.items.filter((_, j) => j !== i);
            if (items.length === 0) onRemove();
            else onChange({ ...group, items });
          }}
        />
      ))}
      <div className="idsk-button-group">
        <Button variant="secondary" onClick={() => onChange({ ...group, items: [...group.items, emptyLeaf()] })}>
          {t('builder.rules.addToGroup', { n })}
        </Button>
        <Button variant="warning" onClick={onRemove}>
          {t('builder.rules.removeGroup', { n })}
        </Button>
      </div>
    </Fieldset>
  );
}

function ConditionRow({
  id,
  n,
  leaf,
  candidates,
  context,
  teamKnown,
  errors,
  onChange,
  onRemove,
}: {
  id: string;
  n: number;
  leaf: EditorLeaf;
  candidates: Candidate[];
  context: EvaluationContext;
  teamKnown: boolean;
  errors: Record<string, string>;
  onChange: (l: EditorLeaf) => void;
  onRemove: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const candidate = candidates.find((c) => c.ref === leaf.field);
  const field = candidate?.field;
  const ops = operatorChoices(field, teamKnown);
  const candidateLabel = (c: Candidate) => {
    const label = text(c.field.label, lang);
    return c.self ? t('builder.rules.self', { label }) : c.sameRow ? t('builder.rules.sameRow', { label }) : label;
  };
  // the fields a value may be compared with: of the same type, other than the field of the condition
  const comparable = field && FIELD_COMPARISONS.includes(leaf.op as Operator) ? candidates.filter((c) => c.field.type === field.type && c.ref !== leaf.field) : [];
  const byField = leaf.valueField !== undefined;
  const opLabel = (op: Operator) => {
    const dateOrNumber = field?.type === 'DATE' ? 'date' : 'number';
    return ['gt', 'gte', 'lt', 'lte'].includes(op) ? t(`builder.ops.${op}_${dateOrNumber}`) : t(`builder.ops.${op}`);
  };
  return (
    <Fieldset legend={t('builder.rules.conditionLegend', { n })} legendClassName="govuk-fieldset__legend--s" className="app-condition-row">
      <Select
        id={`${id}-field`}
        label={t('builder.rules.field')}
        required
        hint={t('builder.rules.fieldHint')}
        placeholder={t('common.select')}
        options={candidates.map((c) => ({ value: c.ref, label: candidateLabel(c) }))}
        value={leaf.field}
        error={errors[`${id}-field`]}
        onChange={(e) => {
          const next = candidates.find((c) => c.ref === e.target.value);
          const nextOps = operatorChoices(next?.field, teamKnown);
          onChange({ ...leaf, field: e.target.value, op: nextOps.includes(leaf.op as Operator) && next?.field.type === field?.type ? leaf.op : '', value: undefined, valueField: undefined });
        }}
      />
      {field && (
        <Select
          id={`${id}-op`}
          label={t('builder.rules.operator')}
          required
          placeholder={t('common.select')}
          options={ops.map((op) => ({ value: op, label: opLabel(op) }))}
          value={leaf.op}
          error={errors[`${id}-op`]}
          onChange={(e) => {
            const op = e.target.value as Operator;
            // the field compared with stays while the operator still compares values
            onChange({ ...leaf, op, value: undefined, valueField: FIELD_COMPARISONS.includes(op) ? leaf.valueField : undefined });
          }}
        />
      )}
      {field && leaf.op && leaf.op !== 'empty' && leaf.op !== 'notEmpty' && (
        <>
          {/* a fixed value, or the value of another field of the same type (D26) */}
          {(comparable.length > 0 || byField) && (
            <Radios
              name={`${id}-compare`}
              idPrefix={`${id}-compare`}
              legend={t('builder.rules.compareWith')}
              legendClassName="govuk-fieldset__legend--s"
              inline
              items={[
                { value: 'value', label: t('builder.rules.compareValue') },
                { value: 'field', label: t('builder.rules.compareField') },
              ]}
              value={byField ? 'field' : 'value'}
              onChange={(v) => onChange(v === 'field' ? { ...leaf, value: undefined, valueField: '' } : { ...leaf, valueField: undefined })}
            />
          )}
          {byField ? (
            <Select
              id={`${id}-valueField`}
              label={t('builder.rules.valueField')}
              required
              placeholder={t('common.select')}
              options={comparable.map((c) => ({ value: c.ref, label: candidateLabel(c) }))}
              value={leaf.valueField}
              error={errors[`${id}-valueField`]}
              onChange={(e) => onChange({ ...leaf, valueField: e.target.value })}
            />
          ) : (
            <ValueControl id={`${id}-value`} field={field} op={leaf.op} value={leaf.value} context={context} error={errors[`${id}-value`]} onChange={(value) => onChange({ ...leaf, value })} />
          )}
        </>
      )}
      <Button variant="secondary" onClick={onRemove}>
        {t('builder.rules.removeCondition', { n })}
      </Button>
    </Fieldset>
  );
}

function ValueControl({
  id,
  field,
  op,
  value,
  context,
  error,
  onChange,
}: {
  id: string;
  field: FieldDef;
  op: Operator;
  value: JsonValue | undefined;
  context: EvaluationContext;
  error?: string;
  onChange: (v: JsonValue | undefined) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const label = t('builder.rules.value');
  const options = (field.options ?? []).map((o) => ({ value: o.value, label: text(o.label, lang) }));
  const team = (context.teamMembers ?? []).map((m) => ({ value: m.id, label: m.name }));
  const asArray = Array.isArray(value) ? (value as string[]) : [];
  switch (field.type) {
    case 'YES_NO':
    case 'SWITCH':
      return (
        <Radios
          name={id}
          idPrefix={id}
          legend={label}
          required
          legendClassName="govuk-fieldset__legend--s"
          inline
          error={error}
          items={[
            { value: 'true', label: field.type === 'SWITCH' ? t('form.switchOn') : t('common.yes') },
            { value: 'false', label: field.type === 'SWITCH' ? t('form.switchOff') : t('common.no') },
          ]}
          value={typeof value === 'boolean' ? String(value) : null}
          onChange={(v) => onChange(v === 'true')}
        />
      );
    case 'SINGLE_CHOICE':
    case 'TEAM_MEMBER': {
      const items = field.type === 'TEAM_MEMBER' ? team : options;
      if (op === 'in' || op === 'notIn')
        return <Checkboxes name={id} idPrefix={id} legend={label} required legendClassName="govuk-fieldset__legend--s" error={error} items={items} value={asArray} onChange={(v) => onChange(v)} />;
      return <Radios name={id} idPrefix={id} legend={label} required legendClassName="govuk-fieldset__legend--s" error={error} items={items} value={typeof value === 'string' ? value : null} onChange={(v) => onChange(v)} />;
    }
    case 'MULTI_CHOICE':
      if (op === 'eq' || op === 'neq')
        return <Checkboxes name={id} idPrefix={id} legend={label} required legendClassName="govuk-fieldset__legend--s" error={error} items={options} value={asArray} onChange={(v) => onChange(v)} />;
      return <Radios name={id} idPrefix={id} legend={label} required legendClassName="govuk-fieldset__legend--s" error={error} items={options} value={typeof value === 'string' ? value : null} onChange={(v) => onChange(v)} />;
    case 'DATE':
      return (
        <DateInput
          id={id}
          legend={label}
          required
          legendClassName="govuk-fieldset__legend--s"
          error={error}
          value={isoToParts(typeof value === 'string' ? value : '')}
          onChange={(parts) => {
            const r = partsToIso(parts);
            onChange(r.kind === 'valid' ? r.iso : r.kind === 'empty' ? undefined : `${parts.year}-${parts.month}-${parts.day}`);
          }}
        />
      );
    case 'NUMBER':
      return <NumberValue id={id} label={label} error={error} value={value} lang={lang} onChange={onChange} />;
    default:
      return <Input id={id} label={label} required error={error} value={typeof value === 'string' ? value : ''} width="two-thirds" onChange={(e) => onChange(e.target.value)} />;
  }
}

function NumberValue({ id, label, error, value, lang, onChange }: { id: string; label: string; error?: string; value: JsonValue | undefined; lang: Lang; onChange: (v: JsonValue | undefined) => void }) {
  return (
    <Input
      id={id}
      label={label}
      required
      error={error}
      inputMode="decimal"
      width="10"
      defaultValue={displayNumber(value, lang)}
      onBlur={(e) => {
        const canonical = toCanonicalNumber(e.target.value);
        onChange(canonical === '' ? undefined : canonical);
      }}
    />
  );
}
